/**
 * Bridge Client
 *
 * Enables the web app to communicate with the Chrome extension through
 * a postMessage-based protocol. This allows the web app to access Chrome
 * APIs indirectly through the extension's service worker.
 *
 * Message flow:
 * Web App -> postMessage -> Bridge (sandbox.html) -> chrome.runtime -> Service Worker
 */

// ============================================================================
// Types
// ============================================================================

export type BridgeCall =
  | 'GET_AUTH_TOKEN'
  | 'SIGN_OUT'
  | 'GET_TAB_INFO'
  | 'GET_CASE_DATA'
  | 'JUMP_TO_CITATION'
  | 'STORAGE_GET'
  | 'STORAGE_SET'
  | 'STORAGE_REMOVE'
  | 'GET_EXTENSION_INFO'
  | 'OPEN_OPTIONS'
  | 'OPEN_URL';

export interface InitialContext {
  tabId: number | null;
  tabUrl: string | null;
  isCanLIIPage: boolean;
  online: boolean;
  extensionId: string;
}

export interface CaseMetadata {
  title: string;
  citation: string;
  url: string;
  court?: string;
  date?: string;
}

export interface TextChunk {
  text: string;
  index: number;
  textStart: string;
}

export interface CaseData {
  metadata: CaseMetadata;
  chunks: TextChunk[];
}

export interface CaseDataResponse {
  success: boolean;
  data?: CaseData;
  error?: string;
}

type EventCallback = (data: unknown) => void;

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeoutId: number;
}

// ============================================================================
// Bridge Client Class
// ============================================================================

class BridgeClient {
  private ready = false;
  private readyCallbacks: Array<() => void> = [];
  private pendingRequests = new Map<string, PendingRequest>();
  private eventHandlers = new Map<string, Set<EventCallback>>();
  private initialContext: InitialContext | null = null;
  private online = true;

  private readonly REQUEST_TIMEOUT_MS = 30000;

  constructor() {
    // Listen for messages from the extension bridge
    window.addEventListener('message', this.handleMessage.bind(this));

    // Signal that we're ready
    this.signalReady();
  }

  /**
   * Signal to the extension that the web app is loaded and ready
   */
  private signalReady(): void {
    // Target origin '*' is required for sandboxed iframe communication
    // (sandbox origin is 'null' which can't be specified as targetOrigin).
    // Security is ensured by only accepting responses from window.parent (see handleMessage).
    window.parent.postMessage({ type: 'READY' }, '*');
  }

  /**
   * Handle incoming messages from the extension bridge
   */
  private handleMessage(event: MessageEvent): void {
    // Security: only accept messages from our parent frame (the extension sandbox).
    // Sandboxed iframes report origin as 'null' (string), so we can't rely on
    // origin-based validation alone. Checking event.source ensures we only process
    // messages sent by the parent frame that hosts us.
    if (event.source !== window.parent) return;

    const message = event.data;
    if (!message || typeof message !== 'object') return;

    // Handle initial context from bridge
    if (message.type === 'INITIAL_CONTEXT') {
      this.initialContext = message.data as InitialContext;
      this.online = message.data?.online ?? true;
      this.ready = true;

      // Notify all ready callbacks
      this.readyCallbacks.forEach(cb => cb());
      this.readyCallbacks = [];
      return;
    }

    // Handle bridge call response
    if (message.callbackId) {
      const pending = this.pendingRequests.get(message.callbackId);
      if (pending) {
        this.pendingRequests.delete(message.callbackId);
        window.clearTimeout(pending.timeoutId);

        if (message.error) {
          pending.reject(new Error(message.error));
        } else {
          pending.resolve(message.result);
        }
      }
      return;
    }

    // Handle network status updates
    if (message.type === 'NETWORK_STATUS') {
      this.online = message.online as boolean;
      this.emit('networkStatus', { online: this.online });
      return;
    }

    // Handle events forwarded from extension
    if (message.type === 'EVENT') {
      this.emit(message.event, message.data);
      return;
    }

    // Handle pong (connection check response)
    if (message.type === 'PONG') {
      return;
    }
  }

  /**
   * Register a callback for when the bridge is ready
   */
  onReady(callback: () => void): void {
    if (this.ready) {
      callback();
    } else {
      this.readyCallbacks.push(callback);
    }
  }

  /**
   * Check if bridge is ready
   */
  isReady(): boolean {
    return this.ready;
  }

  /**
   * Check if online
   */
  isOnline(): boolean {
    return this.online;
  }

  /**
   * Get the initial context received from the extension
   */
  getInitialContext(): InitialContext | null {
    return this.initialContext;
  }

  /**
   * Send a bridge call to the extension
   */
  send<T>(call: BridgeCall, params: Record<string, unknown> = {}): Promise<T> {
    return new Promise((resolve, reject) => {
      const callbackId = crypto.randomUUID();

      // Set up timeout
      const timeoutId = window.setTimeout(() => {
        this.pendingRequests.delete(callbackId);
        reject(new Error(`Bridge call timeout: ${call}`));
      }, this.REQUEST_TIMEOUT_MS);

      // Store pending request
      this.pendingRequests.set(callbackId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeoutId,
      });

      // Send message to bridge.
      // Target origin '*' is required for sandboxed iframe communication
      // (sandbox origin is 'null' which can't be specified as targetOrigin).
      // Security is ensured by only accepting responses from window.parent (see handleMessage).
      window.parent.postMessage({ call, params, callbackId }, '*');
    });
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get Google OAuth token
   */
  async getAuthToken(interactive = false): Promise<string | null> {
    return this.send<string | null>('GET_AUTH_TOKEN', { interactive });
  }

  /**
   * Sign out (clear cached tokens)
   */
  async signOut(): Promise<void> {
    await this.send<{ success: boolean }>('SIGN_OUT');
  }

  /**
   * Get current tab info
   */
  async getTabInfo(): Promise<{ tabId: number | null; tabUrl: string | null; isCanLIIPage: boolean } | null> {
    return this.send('GET_TAB_INFO');
  }

  /**
   * Get case data from the content script
   */
  async getCaseData(): Promise<CaseDataResponse | null> {
    return this.send<CaseDataResponse | null>('GET_CASE_DATA');
  }

  /**
   * Jump to a citation/paragraph in the case document
   */
  async jumpToCitation(paragraph: number, textStart?: string): Promise<{ success: boolean }> {
    return this.send<{ success: boolean }>('JUMP_TO_CITATION', { paragraph, textStart });
  }

  /**
   * Get values from chrome.storage.local
   */
  async storageGet<T = Record<string, unknown>>(keys: string | string[]): Promise<T> {
    return this.send<T>('STORAGE_GET', { keys });
  }

  /**
   * Set values in chrome.storage.local
   */
  async storageSet(items: Record<string, unknown>): Promise<void> {
    await this.send<{ success: boolean }>('STORAGE_SET', { items });
  }

  /**
   * Remove values from chrome.storage.local
   */
  async storageRemove(keys: string | string[]): Promise<void> {
    await this.send<{ success: boolean }>('STORAGE_REMOVE', { keys });
  }

  /**
   * Get extension info (ID, version)
   */
  async getExtensionInfo(): Promise<{ id: string; version: string }> {
    return this.send('GET_EXTENSION_INFO');
  }

  /**
   * Open extension options page
   */
  async openOptions(): Promise<void> {
    await this.send<{ success: boolean }>('OPEN_OPTIONS');
  }

  /**
   * Open a URL in a new tab (bypasses iframe restrictions for hash fragments)
   */
  async openUrl(url: string): Promise<void> {
    await this.send<{ success: boolean }>('OPEN_URL', { url });
  }

  // ============================================================================
  // Event System
  // ============================================================================

  /**
   * Subscribe to an event
   */
  on(event: string, handler: EventCallback): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Unsubscribe from an event
   */
  off(event: string, handler: EventCallback): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  /**
   * Emit an event to all handlers
   */
  private emit(event: string, data: unknown): void {
    this.eventHandlers.get(event)?.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('[Bridge] Event handler error:', error);
      }
    });
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const bridge = new BridgeClient();
